import {
  Alert,
  Button,
  EmptyState,
  FormField,
  Section,
  Skeleton,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ColonyPicker } from '../apiary/components/ColonyPicker';
import { useColonies, usePhoto, useSaveNote } from '../apiary/api';
import { useSession } from '../identity/session';
import { errorMessage } from '../../lib/errors';
import { isQueued } from '../offline/offline';
import { clearShared, noteText, readShared, type Shared } from './share';
import styles from './SharePage.module.css';

/** Photos cannot be filed while the notes only wait in the offline queue. */
class ShareOffline extends Error {}

/**
 * Target of "Share" from other apps (Web Share Target): photos and text go to
 * the chosen colonies as a note, one note per photo.
 */
export function SharePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const colonies = useColonies({}, can('colonies.write'));
  const saveNote = useSaveNote();
  const photo = usePhoto('colony-notes');
  const [shared, setShared] = useState<Shared | null | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Notes (and their photos) already filed by an earlier, failed attempt:
  // a retry continues instead of filing them twice.
  const filed = useRef(new Map<string, string>());
  const withPhoto = useRef(new Set<string>());

  useEffect(() => {
    let urls: string[] = [];
    void readShared().then((found) => {
      urls = found?.photos.map((p) => p.url) ?? [];
      setShared(found);
      if (found) setText(noteText(found, ''));
    });
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  if (!can('colonies.write')) {
    return <EmptyState size="page" level={1} title={t('share.noPermission')} />;
  }
  if (shared === undefined || colonies.isPending) return <Skeleton lines={4} />;
  if (shared === null || (shared.photos.length === 0 && !text.trim() && !shared.text)) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={t('share.empty')}
        actions={<Link to="/">{t('notFound.back')}</Link>}
      />
    );
  }

  const file = async () => {
    setError(null);
    setBusy(true);
    try {
      const body = text.trim() || t('share.photoNote');
      for (const colonyId of selected) {
        const entries = shared.photos.length > 0 ? shared.photos : [null];
        for (const [index, item] of entries.entries()) {
          const key = `${colonyId}/${index}`;
          let id = filed.current.get(key);
          if (id === undefined) {
            const note = await saveNote.mutateAsync({
              colonyId,
              body: { noted_on: todayIso(), text: body },
            });
            // Captured offline: photos cannot follow, the note waits in the queue.
            if (isQueued(note)) {
              filed.current.set(key, '');
              if (item) throw new ShareOffline();
              continue;
            }
            id = (note as { id: string }).id;
            filed.current.set(key, id);
          }
          if (item && id && !withPhoto.current.has(key)) {
            await photo.mutateAsync({
              id,
              file: new File([item.blob], item.name, { type: item.blob.type }),
            });
            withPhoto.current.add(key);
          }
        }
      }
      await clearShared();
      // Captured offline: one toast that says so instead of "saved" (#190).
      if ([...filed.current.values()].includes('')) toast.show(t('offline.captured'), 'info');
      else toast.show(t('share.saved', { count: selected.length }));
      void navigate(selected.length === 1 ? `/voelker/${selected[0]}` : '/voelker', {
        replace: true,
      });
    } catch (e) {
      setError(e instanceof ShareOffline ? t('share.offline') : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={t('share.title')} variant="content">
      {error && <Alert tone="danger">{error}</Alert>}
      {shared.photos.length > 0 && (
        <ul className={styles.photos} aria-label={t('share.photos')}>
          {shared.photos.map((p) => (
            <li key={p.url}>
              <img src={p.url} alt={p.name} className={styles.photo} />
            </li>
          ))}
        </ul>
      )}
      <FormField label={t('share.text')}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={text}
            maxLength={4000}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </FormField>
      <ColonyPicker
        colonies={colonies.data ?? []}
        selected={selected}
        onChange={setSelected}
        required
      />
      <div className={styles.actions}>
        <Button loading={busy} disabled={selected.length === 0} onClick={() => void file()}>
          {t('share.save')}
        </Button>
        <Button
          variant="plain"
          onClick={() => {
            void clearShared().then(() => navigate('/', { replace: true }));
          }}
        >
          {t('share.discard')}
        </Button>
      </div>
    </Section>
  );
}
